import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

/**
 * styled nav element
 * @memberof Pagination
 * @type {styled-component}
 */
const PaginationContainer = styled.nav`
  display: flex;
  margin: var(--spectrum-global-dimension-size-500) 0;
  justify-content: center;
  align-items: center;
`;

/**
 * styled button used for Next and Previous
 * @memberof Pagination
 * @type {styled-component}
 */
const PaginationButton = styled.button`
  font-family: inherit;
  border: 0;
  background: transparent;
  font-weight: bold;
  margin: 0 var(--spectrum-global-dimension-size-300);
  color: ${(props) =>
    props.disabled ? 'var(--spectrum-global-color-gray-500)' : 'var(--spectrum-global-color-gray-800)'};
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
`;

/**
 * styled button used for page numbers
 * @memberof Pagination
 * @type {styled-component}
 */
const PaginationItem = styled.button`
  --text-color: var(--spectrum-global-color-gray-800);
  --bg-color: transparent;
  --hover-bg-color: var(--spectrum-global-color-gray-600);
  --hover-text-color: white;
  font-family: inherit;
  border: 0;
  margin: 0 var(--spectrum-global-dimension-size-50);
  width: var(--spectrum-global-dimension-size-400);
  height: var(--spectrum-global-dimension-size-400);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  color: var(--text-color);
  background: var(--bg-color);
  cursor: pointer;
  &.selected {
    --bg-color: var(--hover-bg-color);
    --text-color: var(--hover-text-color);
    cursor: default;
    font-weight: bold;
    font-size: var(--spectrum-global-dimension-font-size-200);
  }
  &:hover {
    --bg-color: var(--hover-bg-color);
    --text-color: var(--hover-text-color);
    font-weight: bold;
  }
`;

/**
 * container for the dots displayed between last/first page and the other pages
 * @memberof Pagination
 * @type {styled-component}
 */
const DotsContainer = styled.span`
  font-family: inherit;
  margin: 0 var(--spectrum-global-dimension-size-50);
  width: var(--spectrum-global-dimension-size-400);
  height: var(--spectrum-global-dimension-size-400);
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--spectrum-global-color-gray-800);
`;

/**
 * Pagination component that displays Prev and Next buttons as well as page buttons
 * @component
 * @category Shared Components
 * @subcategory EXC
 * @prop {number} [defaultPage=1] - initial selected page, a change to this prop will trigger a change to the internal page state
 * @prop {number} totalPages - number of all pages
 * @prop {number} [maxAdjacentPageNumbers=3] - use this to set how many adjacent page numbers to your current page should be shown
 * @prop {boolean} [enablePageNumbers=true] - use this to set how many adjacent page numbers to your current page should be shown
 * @prop {function} onChange - function to call when the page is changed; receives the page number as argument
 * @prop {function} [onNext=onChange] - function to call when the Next button is pressed; receives the page number as argument
 * @prop {function} [onPrevious=onChange] - function to call when the Previous button is pressed; receives the page number as argument
 * @prop {...*} [rest] - other props will be passed dirrectly to the Flex container
 * @example
 * return (
 *    <Pagination totalPages={24} onChange={()=>{}} />
 * )
 */
const Pagination = (props) => {
  const {
    defaultPage = 1,
    totalPages,
    onChange,
    onPrevious,
    onNext,
    maxAdjacentPageNumbers = 3,
    enablePageNumbers = true,
    ...rest
  } = props;
  const [currentPage, setCurrentPage] = useState(defaultPage);
  const isPreviousDisabled = currentPage === 1;
  const isNextDisabled = currentPage === totalPages;
  const pageNumbers = useMemo(
    () =>
      totalPages > 2
        ? Array(totalPages - 2)
            .fill(0)
            .map((_, i) => i + 2)
        : [],
    [totalPages]
  );

  useEffect(() => {
    setCurrentPage(defaultPage);
  }, [defaultPage]);

  const onPageChange = (page, type = 'change') => {
    setCurrentPage(page);
    switch (type) {
      case 'change':
        onChange(page);
        break;
      case 'next':
        onNext ? onNext(page) : onChange(page);
        break;
      case 'previous':
        onPrevious ? onPrevious(page) : onChange(page);
        break;
      default:
        break;
    }
  };

  const renderShownPageNumbers = (page) => {
    let shownPageNumbers = [];
    const maxAdjacentPageNumbersDouble = maxAdjacentPageNumbers * 2;
    // 2 from first and last page
    // 1 from the current page
    if (maxAdjacentPageNumbers === 0) {
      shownPageNumbers = currentPage !== 1 && currentPage !== totalPages ? [currentPage] : [];
    } else if (totalPages <= 2 + 1 + maxAdjacentPageNumbersDouble) {
      shownPageNumbers = pageNumbers;
    } else {
      let previousPagesAvailable = page - 2;
      let nextPagesAvailable = totalPages - page - 1;
      if (previousPagesAvailable < maxAdjacentPageNumbers) {
        nextPagesAvailable = maxAdjacentPageNumbersDouble - previousPagesAvailable;
      }
      if (nextPagesAvailable < maxAdjacentPageNumbers) {
        previousPagesAvailable = maxAdjacentPageNumbersDouble - nextPagesAvailable;
      }
      if (previousPagesAvailable + nextPagesAvailable > maxAdjacentPageNumbersDouble) {
        previousPagesAvailable =
          previousPagesAvailable < maxAdjacentPageNumbers ? previousPagesAvailable : maxAdjacentPageNumbers;
        nextPagesAvailable = nextPagesAvailable < maxAdjacentPageNumbers ? nextPagesAvailable : maxAdjacentPageNumbers;
      }
      const pageIndex = page - 2;
      shownPageNumbers = pageNumbers.slice(pageIndex - previousPagesAvailable, pageIndex + nextPagesAvailable + 1);
    }
    return (
      <>
        {shownPageNumbers[0] !== 2 && totalPages > 2 && <DotsContainer>. . .</DotsContainer>}
        {shownPageNumbers.map((p) => (
          <PaginationItem
            onClick={() => !(currentPage === p) && onPageChange(p)}
            key={`page-item${p}`}
            aria-label={generateAriaLabel(p, currentPage)}
            className={[currentPage === p && 'selected']}
          >
            {p}
          </PaginationItem>
        ))}
        {shownPageNumbers[shownPageNumbers.length - 1] !== totalPages - 1 && totalPages > 2 && (
          <DotsContainer>. . .</DotsContainer>
        )}
      </>
    );
  };

  const generateAriaLabel = (page, currentPage) => {
    let ariaLabelText;
    if (page === currentPage) {
      ariaLabelText = `Current page, page ${page}`;
    } else {
      ariaLabelText = `Go to page ${page}`;
    }
    return ariaLabelText;
  };

  return (
    <PaginationContainer aria-label="Pagination" role="navigation" {...rest}>
      <PaginationButton
        disabled={isPreviousDisabled}
        onClick={() => !isPreviousDisabled && onPageChange(currentPage - 1, 'previous')}
        aria-label="Go to Previous page"
      >
        Previous
      </PaginationButton>
      {enablePageNumbers && (
        <>
          <PaginationItem
            onClick={() => !isPreviousDisabled && onPageChange(1)}
            key="first-page"
            aria-label={generateAriaLabel(1, currentPage)}
            className={[currentPage === 1 && 'selected']}
          >
            1
          </PaginationItem>
          {renderShownPageNumbers(currentPage)}
          <PaginationItem
            onClick={() => !isNextDisabled && onPageChange(totalPages)}
            key="last-page"
            aria-label={`${generateAriaLabel(totalPages, currentPage)}, the last page`}
            className={[currentPage === totalPages && 'selected']}
          >
            {totalPages}
          </PaginationItem>
        </>
      )}
      <PaginationButton
        disabled={isNextDisabled}
        onClick={() => !isNextDisabled && onPageChange(currentPage + 1, 'next')}
        aria-label="Go to Next page"
      >
        Next
      </PaginationButton>
    </PaginationContainer>
  );
};

export default Pagination;
